import { Breadcrumb, BreadcrumbItem } from "flowbite-react";
import { HiHome, HiFilter, HiPlus } from "react-icons/hi";
import { useEffect, useRef, useState } from "react";
import Loader from "../../Components/Loader/Loader";

import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getCookie } from "../../reducer/utils";
import FutureOriginalCategoryFilter from "../../Components/SideFilters/FutureOriginalCategoryFilter";
import FutureOriginalCard from "./FutureOriginalCard";
import CreateFutureOriginal from "../../Components/SideFilters/CreateFutureOriginal";
import { TablePagination } from "../../Components/Pagination/Pagination";

function FutureOriginal() {
  const baseURL = process.env.REACT_APP_BASE_URL;

  const [roles, setRoles] = useState(getCookie("roles"));

  const toastId = useRef(null);
  const checkRole = (role) => {
    return roles.includes(role);
  };

  const [showFutureOriginalFilter, setShowFutureOriginalFilter] =
    useState(false);
  const [futureOriginals, setFutureOriginals] = useState([]);

  const [showCreateFutureOriginal, setShowCreateFutureOriginal] =
    useState(false);

  const [haveMoreResults, setHaveMoreResults] = useState(false);
  const [pagination, setPagination] = useState({
    start: 0,
    page_size: 50,
  });

  const [filter, setFilter] = useState({});

  // The "Hidden only" filter is applied server-side: the filter component puts
  // an `is_hidden` clause into the payload, and hidden FOs are excluded from the
  // default list by the API.
  const onApplyFilter = (filter_payload) => {
    setPagination({ ...pagination, start: 0 });
    setFilter({ ...filter_payload });
    setShowFutureOriginalFilter(false);
  };

  // After the card hides/unhides or deletes a FO via the API, refresh the list.
  // A hidden FO drops out of the default list (the API excludes it), and an
  // unhidden one drops out of the "Hidden only" list.
  const refreshList = () => {
    getFutureOriginal();
  };

  const getFutureOriginal = async () => {
    toastId.current = toast.loading("Fetching future originals...");

    const response = await fetch(
      `${baseURL}/api/v4/vod/future-originals/filter?include_episode_status=true`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${getCookie("token")}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...filter,
          pagination_metadata: pagination,
        }),
      },
    );
    if (!response.ok) {
      // Reset to safe defaults on error (e.g. 403) so the UI doesn't crash.
      setFutureOriginals([]);
      setHaveMoreResults(false);
      toast.update(toastId.current, {
        render:
          response.status === 401 || response.status === 403
            ? "Session expired. Please log in again."
            : "Failed to fetch future originals",
        type: "error",
        isLoading: false,
        autoClose: 2000,
      });
      return;
    }

    const data = await response.json();
    setFutureOriginals(data?.["future_originals"] ?? []);
    setHaveMoreResults(data?.["pagination_metadata"]?.["has_more"] ?? false);

    toast.update(toastId.current, {
      render: "Categories fetched successfully",
      type: "success",
      isLoading: false,
      autoClose: 1000,
    });
  };

  const [pageNo, setPageNo] = useState(0);

  useEffect(() => {
    // Skip the initial render
    if (pageNo === 0 && pagination.start === 0) return;

    setPagination({ ...pagination, start: pageNo * pagination.page_size });
  }, [pageNo]);

  useEffect(() => {
    getFutureOriginal();
  }, [filter, pagination]);

  const notify = (message) => toast(message);

  return (
    <>
      <ToastContainer />

      <FutureOriginalCategoryFilter
        showCategoryFilter={showFutureOriginalFilter}
        setShowCategoryFilter={setShowFutureOriginalFilter}
        onApplyFilter={onApplyFilter}
      />
      <CreateFutureOriginal
        showFilter={showCreateFutureOriginal}
        setShowFilter={setShowCreateFutureOriginal}
        notify={notify}
        getFutureOriginal={getFutureOriginal}
      />

      <div className="flex items-center justify-between rounded-lg border-2 border-solid border-grey1 bg-white px-4 py-4">
        <Breadcrumb aria-label="Default breadcrumb example">
          <BreadcrumbItem href="/dashboard" icon={HiHome} />
          <BreadcrumbItem className="text-primary1">Home</BreadcrumbItem>
        </Breadcrumb>
      </div>

      <div className="mt-4 rounded-lg bg-white p-6 shadow-md">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold">Future Originals</h2>
          <div className="flex space-x-2">
            {
              checkRole("internal") &&
              <button
                className="flex items-center gap-2 rounded-md border bg-primary1 px-4 py-2 text-white"
                onClick={() => setShowCreateFutureOriginal(true)}
              >
                <HiPlus />
                <span className="mr-2">Add Future Original</span>
              </button>
            }
            <button
              className="flex items-center gap-2 rounded-md border px-4 py-2"
              onClick={() => setShowFutureOriginalFilter(true)}
            >
              <HiFilter />
              <span className="mr-2">Filter</span>
            </button>
          </div>
        </div>

        <div className="flex flex-col gap-4">
          {futureOriginals?.map((futureOriginal, index) => (
            <FutureOriginalCard
              futureOriginal={futureOriginal}
              key={futureOriginal.id}
              onToggleHidden={refreshList}
              onDelete={refreshList}
            />
          ))}
        </div>
        <TablePagination
          setPageNo={setPageNo}
          page_size={pagination.page_size}
          start={pagination.start}
          has_more={haveMoreResults}
        />
      </div>
    </>
  );
}

export default FutureOriginal;
